import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/hooks/useAuth'
import { adImageService } from '@/services/ad_image_service'
import { AdImageWithTag, Advertisement } from '@magnetar/admin-api-interfaces'
import { logger } from '@magnetar/utils'

interface EditAdImageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierId: number
  image: AdImageWithTag | null
  advertisements: Advertisement[]
  onSuccess?: () => void
}

export default function EditAdImageDialog({
  open,
  onOpenChange,
  supplierId,
  image,
  advertisements,
  onSuccess,
}: EditAdImageDialogProps) {
  const { token } = useAuth()
  const [tagAdId, setTagAdId] = useState('none')
  const [duration, setDuration] = useState('10')
  const [isActive, setIsActive] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (image) {
      setTagAdId(image.tag_ad_id ? String(image.tag_ad_id) : 'none')
      setDuration(image.play_duration_ms ? String(Math.round(image.play_duration_ms / 1000)) : '10')
      setIsActive(image.is_active)
    }
  }, [image])

  const handleSubmit = async () => {
    if (!image || !token) return

    setIsSaving(true)
    try {
      const response = await adImageService.updateAdImage(
        supplierId,
        image.ad_image_id,
        {
          tag_ad_id: tagAdId === 'none' ? null : parseInt(tagAdId, 10),
          play_duration_ms: (parseInt(duration, 10) || 0) * 1000,
          is_active: isActive,
        },
        token,
      )

      if (response.success) {
        toast.success('廣告圖片已更新')
        onSuccess?.()
      } else {
        toast.error(response.error?.message || '更新廣告圖片失敗')
      }
    } catch (error) {
      logger.error('更新廣告圖片失敗', error)
      toast.error('更新廣告圖片失敗')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>編輯廣告圖片</DialogTitle>
          <DialogDescription>設定標記廣告、播放時長與啟用狀態</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>標記廣告</Label>
            <Select value={tagAdId} onValueChange={setTagAdId}>
              <SelectTrigger>
                <SelectValue placeholder="選擇要標記的廣告" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">不標記</SelectItem>
                {advertisements.map((ad) => (
                  <SelectItem key={ad.advertisement_id} value={String(ad.advertisement_id)}>
                    #{ad.advertisement_id} {ad.title || '無標題廣告'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="duration">播放時長（秒）</Label>
            <Input
              id="duration"
              type="number"
              min="1"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="請輸入秒數"
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <Label>啟用狀態</Label>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? '儲存中...' : '儲存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
